import React, { useEffect, useMemo, useState } from "react";
import { MagnifyingGlass } from "react-loader-spinner";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import "@fortawesome/fontawesome-free/css/all.min.css";
import assortment from "../../images/almoune/livraison-gratuite.jpg";
import ScrollToTop from "../ScrollToTop";
import { loadStorefrontProducts } from "../../data/storefront";
import { addProductToCart, getSuggestedCartAmount } from "../../data/cart";
import { categoryTree, getCategoryChildren } from "../../data/categoryTree";

function Shop() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const paramCategory = searchParams.get("category");
  const [loaderStatus, setLoaderStatus] = useState(true);
  const [products, setProducts] = useState([]);
  const [activeCategory, setActiveCategory] = useState("Toutes");
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    let isMounted = true;

    const fetchProducts = async () => {
      const loadedProducts = await loadStorefrontProducts();
      if (!isMounted) {
        return;
      }
      setProducts(loadedProducts);
      if (paramCategory) {
        setActiveCategory(paramCategory);
      }
      setTimeout(() => {
        if (isMounted) {
          setLoaderStatus(false);
        }
      }, 1500);
    };

    fetchProducts();

    return () => {
      isMounted = false;
    };
  }, [paramCategory]);

  const activeCategorySet = useMemo(() => {
    if (!activeCategory || activeCategory === "Toutes") return null;
    return new Set(getCategoryChildren(activeCategory));
  }, [activeCategory]);

  const filteredProducts = useMemo(() => {
    return products.filter((product) => {
      let matchCategory = true;
      if (activeCategorySet && activeCategory !== "Toutes") {
        matchCategory = Array.isArray(product.category)
          ? product.category.some(cat => activeCategorySet.has(cat))
          : activeCategorySet.has(product.category);
      }
      const catStr = Array.isArray(product.category) ? product.category.join(" ") : (product.category || "");
      const searchSource = `${product.name} ${catStr}`.toLowerCase();
      const matchSearch = searchSource.includes(searchTerm.toLowerCase());
      return matchCategory && matchSearch;
    });
  }, [activeCategory, activeCategorySet, products, searchTerm]);

  const handleCategorySelect = (category) => {
    setActiveCategory(category);
  };

  const handleAddToCart = async (product) => {
    try {
      await addProductToCart(product, getSuggestedCartAmount(product));
      navigate("/ShopCart");
    } catch (error) {
      window.alert(error.message);
    }
  };

  return (
    <div>
      {loaderStatus ? (
        <div className="loader-container">
          <MagnifyingGlass visible={true} height="100" width="100" ariaLabel="magnifying-glass-loading" wrapperStyle={{}} wrapperclassName="magnifying-glass-wrapper" glassColor="#c0efff" color="#0aad0a" />
        </div>
      ) : (
        <>
          <ScrollToTop />
          <div className="container">
            <div className="row">
              <h5 className="mb-3 mt-8">Categories</h5>
              <div className="col-md-3">
                <div className="mb-3">
                  <button
                    type="button"
                    className={`btn btn-sm w-100 ${
                      activeCategory === "Toutes"
                        ? "btn-primary"
                        : "btn-outline-dark"
                    }`}
                    onClick={() => handleCategorySelect("Toutes")}
                  >
                    Toutes les categories
                  </button>
                </div>
                {categoryTree.map((top) => (
                  <ul className="nav flex-column" key={top.slug}>
                    <li className="nav-item">
                      <button
                        type="button"
                        className={`nav-link btn btn-link text-start w-100 text-decoration-none ${
                          activeCategory === top.name ? "fw-bold text-dark" : ""
                        }`}
                        onClick={() => handleCategorySelect(top.name)}
                      >
                        {top.name}
                      </button>
                      <ul className="nav flex-column ms-3">
                        {top.children.map((child) => (
                          <li className="nav-item" key={child.slug}>
                            <button
                              type="button"
                              className={`nav-link btn btn-link text-start text-decoration-none ${
                                activeCategory === child.name ? "fw-semibold text-dark" : ""
                              }`}
                              onClick={() => handleCategorySelect(child.name)}
                            >
                              {child.name}
                            </button>
                          </li>
                        ))}
                      </ul>
                    </li>
                  </ul>
                ))}

                <div className="py-4">
                    <h5 className="mb-3">Boutique</h5>
                  <div className="my-4">
                    <input
                      type="search"
                      className="form-control"
                      placeholder="Rechercher un produit"
                      value={searchTerm}
                      onChange={(event) => setSearchTerm(event.target.value)}
                    />
                  </div>
                  <div className="form-check mb-2">
                    <input className="form-check-input" type="checkbox" id="almouneStore" defaultChecked />
                    <label className="form-check-label" htmlFor="almouneStore">Almoune</label>
                  </div>
                  <div className="form-check mb-2">
                    <input className="form-check-input" type="checkbox" id="promoStore" />
                    <label className="form-check-label" htmlFor="promoStore">Promotions</label>
                  </div>
                  <div className="form-check mb-2">
                    <input className="form-check-input" type="checkbox" id="freshStore" />
                    <label className="form-check-label" htmlFor="freshStore">Produits frais</label>
                  </div>
                </div>

                <div className="py-4">
                  <Link to="/Shop">
                      <img src={assortment} alt="Livraison Almoune et assortiment oriental" className="img-fluid rounded-3" />
                  </Link>
                </div>
              </div>

              <div className="col-lg-9 col-md-8">
                <div className="card mb-4 bg-light border-0">
                  <div className="card-body p-9">
                    <h1 className="mb-0">Boutique Almoune</h1>
                    <p className="mb-0 mt-2">Retrouvez notre selection de produits libanais et orientaux : cafe, boissons, condiments, douceurs, epicerie du quotidien et specialites de boutique avec prix et stock mis a jour.</p>
                  </div>
                </div>

                <div className="d-md-flex justify-content-between align-items-center">
                  <div>
                    <p className="mb-3 mb-md-0"><span className="text-dark">{filteredProducts.length}</span> Produits trouves</p>
                  </div>
                  <div className="d-flex justify-content-between align-items-center">
                    <Link to="/ShopListCol" className="text-muted me-3"><i className="bi bi-list-ul" /></Link>
                    <Link to="/ShopGridCol3" className="me-3 text-muted"><i className="bi bi-grid" /></Link>
                    <Link to="/Shop" className="me-3 active"><i className="bi bi-grid-3x3-gap" /></Link>
                  </div>
                </div>

                <div className="row g-4 row-cols-xl-4 row-cols-lg-3 row-cols-2 row-cols-md-2 mt-2">
                  {filteredProducts.map((product) => (
                    <div className="col" key={product.id}>
                      <div className="card card-product">
                        <div className="card-body">
                          <div className="text-center position-relative ">
                            <div className=" position-absolute top-0 start-0">
                              <span className="badge bg-danger">{product.badge}</span>
                            </div>
                            <Link to="/SingleShop">
                              <img src={product.image} alt={product.name} className="mb-3 img-fluid" />
                            </Link>
                          </div>
                          <div className="text-small mb-1">
                            <Link to="/Shop" className="text-decoration-none text-muted"><small>{product.category}</small></Link>
                          </div>
                          <h2 className="fs-6">
                            <Link to="/SingleShop" className="text-inherit text-decoration-none">{product.name}</Link>
                          </h2>
                          <div>
                            <small className="text-warning">
                              <i className="bi bi-star-fill" />
                              <i className="bi bi-star-fill" />
                              <i className="bi bi-star-fill" />
                              <i className="bi bi-star-fill" />
                              <i className="bi bi-star-half" />
                            </small>{" "}
                            <span className="text-muted small">{product.rating} ({product.reviews})</span>
                          </div>
                          <div className="d-flex justify-content-between align-items-center mt-3">
                            <div>
                              <span className="text-dark">{product.priceLabel}</span>
                            </div>
                            <div>
                              <button type="button" className="btn btn-primary btn-sm" onClick={() => handleAddToCart(product)}>
                                <svg xmlns="http://www.w3.org/2000/svg" width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="feather feather-plus">
                                  <line x1={12} y1={5} x2={12} y2={19} />
                                  <line x1={5} y1={12} x2={19} y2={12} />
                                </svg>{" "}
                                Ajouter
                              </button>
                            </div>
                          </div>
                          <small className="d-block mt-2 text-muted">Stock: {product.stock}</small>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                {!filteredProducts.length ? (
                  <div className="alert alert-light border mt-4" role="alert">
                    Aucun produit ne correspond a cette categorie ou recherche.
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default Shop;
